import { useEffect, useState } from 'react';
import { api, type Asset, type CollectionItem, type Source } from '../lib/api';
import { Thumb, displayName } from './AssetGrid';
import { SourceThumb } from './Record';

/**
 * This episode's collection (093, P1): the assets and sources gathered for
 * it, by the creator or the director. The tray, the footage pickers and the
 * director draw from here first, the library second — so opening a card
 * fetches what the episode uses and not everything the workspace owns.
 */
import { TemplateGallery } from './TemplateGallery';

export function Collection({ cardId, brandId, onChanged }: {
  cardId: string; brandId: string | null;
  /** The ids gathered, for the tray and the pickers. */
  onChanged: (assetIds: string[], sourceIds: string[]) => void;
}) {
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [picking, setPicking] = useState(false);
  // 192: the template store, from the Idea stage — audio and backgrounds land here, recipes in the Lab.
  const [store, setStore] = useState(false);
  const [library, setLibrary] = useState<{ assets: Asset[]; sources: Source[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function take(next: CollectionItem[]) {
    setItems(next);
    onChanged(next.filter((i) => i.asset_id).map((i) => i.asset_id!), next.filter((i) => i.source_id).map((i) => i.source_id!));
  }
  useEffect(() => { void api.collection(cardId).then(take).catch((e) => setError((e as Error).message)); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [cardId]);

  async function openLibrary() {
    setPicking(true);
    if (library) return;
    try {
      const [assets, src] = await Promise.all([api.assets(brandId), api.sources()]);
      setLibrary({ assets: assets.filter((a) => a.status === 'ready' && a.kind !== 'document'), sources: src.sources.filter((s) => s.status === 'ready' && s.mode === 'stored') });
    } catch (e) { setError((e as Error).message); }
  }
  async function add(what: { assetId?: string; sourceId?: string }) {
    setBusy(what.assetId ?? what.sourceId ?? null);
    try { take(await api.collect(cardId, what)); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }
  async function remove(item: CollectionItem) {
    setBusy(item.item_id);
    try { await api.uncollect(cardId, item.item_id); take(items.filter((i) => i.item_id !== item.item_id)); }
    catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }
  async function gatherNow() {
    setBusy('gather'); setNote(null);
    try {
      const r = await api.gatherCollection(cardId);
      take(r.items);
      setNote(r.gathered ? `The director gathered ${r.gathered} from the library by this episode's theme and subject.` : 'Nothing in the library shares words with this episode yet — describe your pictures, or name the theme on the card.');
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  const gatheredAssets = new Set(items.map((i) => i.asset_id).filter(Boolean));
  const gatheredSources = new Set(items.map((i) => i.source_id).filter(Boolean));

  return (
    <section className="tray" data-tool="collection">
      <div className="tray-hd" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0 }}>This episode</h4>
        <span className="chip">{items.length} gathered</span>
        <span style={{ flex: 1 }} />
        <button className="btn tiny" disabled={busy !== null} onClick={() => void gatherNow()} title="The director picks from the library by this episode's theme, subject and title — and says which words matched">
          {busy === 'gather' ? 'Gathering…' : 'Gather with the director'}
        </button>
        <button className="btn tiny" onClick={() => (picking ? setPicking(false) : void openLibrary())}>{picking ? 'Done' : 'Add from the library…'}</button>
        <button className={`btn tiny${store ? ' pri' : ''}`} onClick={() => setStore((v) => !v)} title="192: free templates — a bed, a background, a plate — into this episode">{store ? 'Close templates' : 'From the templates…'}</button>
      </div>
      {store && (
        <div style={{ margin: '4px 0 10px' }}>
          <TemplateGallery brandId={brandId} cardId={cardId} compact
                           onTaken={() => { void api.collection(cardId).then(take).catch((e) => setError((e as Error).message)); }} />
        </div>
      )}
      {error && <div className="err inline">{error}</div>}
      {note && <div className="sub-dim" style={{ margin: '4px 0 8px' }}>{note}</div>}
      {items.length === 0 && !picking && (
        <p className="hint">Nothing gathered yet. Gather with the director, or add from the library — the timeline's tray and the footage pickers start from what is here.</p>
      )}
      {items.length > 0 && (
        <div className="tray-grid">
          {items.map((i) => (
            <div key={i.item_id} className={`tray-item is-${i.asset_kind ?? 'video'}`} title={i.note ?? (i.added_by === 'director' ? 'gathered by the director' : 'gathered by you')}>
              {i.asset_id
                ? <Thumb asset={{ id: i.asset_id, brand_id: brandId, kind: i.asset_kind!, storage_key: i.storage_key!, bytes: null, duration_ms: i.asset_duration_ms, created_at: i.added_at, status: i.asset_status ?? 'ready', media_key: null } as Asset} size={96} />
                : <SourceThumb source={{ id: i.source_id!, status: i.source_status ?? 'ready' } as Source} width={96} />}
              <div className="tray-name" title={i.asset_name ?? i.source_name ?? ''}>{i.asset_name ?? i.source_name ?? i.storage_key?.split('/').pop()}</div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="sub-dim" style={{ fontSize: 11 }} title={i.note ?? ''}>{i.added_by === 'director' ? (i.note?.replace(/^shares: /, 'shares ') ?? 'the director') : 'you'}</span>
                <button className="btn tiny" disabled={busy === i.item_id} onClick={() => void remove(i)} title="Take it out of this episode (it stays in the library)">×</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {picking && library && (
        <div style={{ marginTop: 8 }}>
          <div className="sub-dim">The library — click to add to this episode.</div>
          <div className="tray-grid">
            {library.assets.filter((a) => !gatheredAssets.has(a.id)).map((a) => (
              <button key={a.id} type="button" className={`tray-item is-${a.kind}`} disabled={busy === a.id} onClick={() => void add({ assetId: a.id })} title={`Add ${displayName(a)}`}>
                <Thumb asset={a} size={96} />
                <div className="tray-name">{displayName(a)}</div>
              </button>
            ))}
            {library.sources.filter((s) => !gatheredSources.has(s.id)).map((s) => (
              <button key={s.id} type="button" className="tray-item is-video" disabled={busy === s.id} onClick={() => void add({ sourceId: s.id })} title={`Add ${s.name}`}>
                <SourceThumb source={s} width={96} />
                <div className="tray-name">{s.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
